import _ from "lodash";
import { Map, Record } from "immutable";
import { Moment } from "moment";

import { RestModel, RestRecord, NormalisedEntities } from "@orm/models";

export interface PlaylistsMap {
    [key: string]: {
        vendorKey: string;
        vendorName: string;
        playlists: Playlist[];
    };
}

export interface PlaylistFilters {
    id: number;
    from: Moment;
    to: Moment;
}

export interface VendorInternalState {
    entities: VendorNormalisedEntities;
    playlistFilters: PlaylistFilters;
    playlistTracksList: string[];
}

export interface VendorState {
    vendor: VendorInternalState;
}

interface MetaMap {
    [key: string]: any;
}

export interface IPlaylist extends RestModel {
    id?: number;
    name?: string;
    url?: string;
    vendor?: string;
    vendorId?: string;
    public?: boolean;
    description?: string;
    meta?: MetaMap;
    dspId?: number;
    isTracked?: boolean;
    createdAt?: Date;
    updatedAt?: Date;
    snapshots?: number[] | IPlaylistSnapshot[];
    vendorName?: string;
    imageUrl?: string;
}

export interface IPlaylistSnapshot {
    id?: number;
    numFollowers?: number;
    meta?: MetaMap;
    playlistId?: number;
    createdAt?: Date;
    updatedAt?: Date;
    tracks?: number[] | ITrack[];
    numOwnedTracks?: number;
}

export interface ITrack extends RestModel {
    id?: number;
    title?: string;
    artists?: string;
    album?: string;
    isrc?: string;
    url?: string;
    vendor?: string;
    vendorId?: string;
    meta?: MetaMap;
    trackId?: number;
    pivot?: IPlaylistSnapshotTrack;
    playlists?: number[] | IPlaylist[];
    vendorName?: string;
}

export interface IPlaylistSnapshotTrack {
    playlistSnapshotId?: number;
    vendorTrackId?: number;
    order?: number;
    addedAt?: Date;
    meta?: MetaMap;
}

export interface IPlaylistTrack extends RestModel {
    track: ITrack;
    trackId: number;
    playlist: IPlaylist;
    playlistId: number;
    vendor: string;
    streams?: number;
}

const PlaylistRecord = RestRecord({
    id: undefined,
    name: undefined,
    url: undefined,
    vendor: undefined,
    vendorId: undefined,
    public: undefined,
    description: undefined,
    meta: undefined,
    dspId: undefined,
    isTracked: undefined,
    createdAt: undefined,
    updatedAt: undefined,
    snapshots: undefined,
    vendorName: undefined,
    imageUrl: undefined,
});

const PlaylistSnapshotRecord = Record({
    id: undefined,
    numFollowers: undefined,
    meta: undefined,
    playlistId: undefined,
    createdAt: undefined,
    updatedAt: undefined,
    tracks: undefined,
    numOwnedTracks: undefined,
});

const TrackRecord = RestRecord({
    id: undefined,
    title: undefined,
    artists: undefined,
    album: undefined,
    isrc: undefined,
    url: undefined,
    vendor: undefined,
    vendorId: undefined,
    meta: undefined,
    trackId: undefined,
    pivot: undefined,
    playlists: undefined,
    vendorName: undefined,
});

const PlaylistSnapshotTrackRecord = Record({
    playlistSnapshotId: undefined,
    vendorTrackId: undefined,
    order: undefined,
    addedAt: undefined,
    meta: undefined,
});

const PlaylistTrackRecord = RestRecord({
    track: undefined,
    trackId: undefined,
    playlist: undefined,
    playlistId: undefined,
    vendor: undefined,
    streams: undefined,
});

interface PlaylistImage {
    width: number;
    height: number;
    url: string;
}

export class Playlist extends PlaylistRecord implements IPlaylist {
    public static RELATIONS = ["snapshots"];

    public id: number;
    public name: string;
    public url: string;
    public vendor: string;
    public vendorId: string;
    public public: boolean;
    public description: string;
    public meta: MetaMap;
    public dspId: number;
    public isTracked: boolean;
    public createdAt: Date;
    public updatedAt: Date;
    // Relationships
    public snapshots: number[] | PlaylistSnapshot[];
    // Cached data
    public vendorName: string;
    public imageUrl: string;

    public getViewUrl(): string {
        return this.id ? "/playlists/view/" + this.id : null;
    }

    public getViewText(): string {
        return this.name != null && this.name !== "" ? this.name : this.vendorId;
    }

    public getMediumImage(): string {
        const images = this.getImages();
        let medium: string = null;
        for (const image of images) {
            if (image != null && (medium == null || image.width < 600)) {
                medium = image.url;
            }
        }
        return medium;
    }

    public getImages(): PlaylistImage[] {
        if (this.meta != null && this.meta.images != null) {
            return _.sortBy(this.meta.images, ["width"]);
        }
        return [];
    }

    public getVendorName(): string {
        return getVendorName(this.vendor);
    }
}

export class PlaylistSnapshot extends PlaylistSnapshotRecord implements IPlaylistSnapshot {
    public id: number;
    public numFollowers: number;
    public meta: MetaMap;
    public playlistId: number;
    public createdAt: Date;
    public updatedAt: Date;
    public tracks: number[] | Track[];
    public numOwnedTracks: number;
}

export class Track extends TrackRecord implements ITrack {
    public static RELATIONS = ["pivot", "playlists"];

    public id: number;
    public title: string;
    public artists: string;
    public album: string;
    public isrc: string;
    public url: string;
    public vendor: string;
    public vendorId: string;
    public meta: MetaMap;
    public trackId: number;
    // Relationships
    public pivot: PlaylistSnapshotTrack;
    public playlists: number[] | Playlist[];
    // Cached data
    public vendorName: string;

    public getVendorName(): string {
        return getVendorName(this.vendor);
    }
}

export class PlaylistSnapshotTrack extends PlaylistSnapshotTrackRecord implements IPlaylistSnapshotTrack {
    public playlistSnapshotId: number;
    public vendorTrackId: number;
    public order: number;
    public addedAt: Date;
    public meta: MetaMap;
}

export class PlaylistTrack extends PlaylistTrackRecord implements IPlaylistTrack {
    public trackId: number;
    public playlistId: number;
    public vendor: string;
    public streams?: number;
    // Relationships
    public track: Track;
    public playlist: Playlist;
}

export interface VendorNormalisedEntities extends NormalisedEntities {
    playlists?: Map<number, Playlist>;
    playlistSnapshots?: Map<number, PlaylistSnapshot>;
    vendorTracks?: Map<number, Track>;
    playlistSnapshotTracks?: Map<string, PlaylistSnapshotTrack>;
    playlistTracks?: Map<string, PlaylistTrack>;
}

export const getVendorName = (vendor: string): string => {
    switch (vendor) {
        case "spotify":
            return "Spotify";
        case "apple-music":
            return "Apple Music";
        case "deezer":
            return "Deezer";
        case "napster":
            return "Napster";
        default:
            return vendor;
    }
};
